/**
 * Reminders Tab — rich UI with stats banner, search, sectioned list,
 * overdue alerts, progress bar, and auto-fetch on tab focus.
 */

import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useFocusEffect } from 'expo-router';
import {
  Alert,
  ActivityIndicator,
  View,
  SectionList,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  RefreshControl,
  LayoutAnimation,
  ScrollView,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { Ionicons } from '@expo/vector-icons';
import { useShallow } from 'zustand/react/shallow';
import { useRemindersStore } from '../../src/stores/reminderStore';
import { useChatStore } from '../../src/stores/chatStore';
import { useAuthStore } from '../../src/stores/authStore';
import { useNavigationStore } from '../../src/stores/navigationStore';
import { useSettingsStore } from '../../src/stores/settingsStore';
import ReminderRow from '../../src/components/reminders/ReminderRow';
import ReminderFilters from '../../src/components/reminders/ReminderFilters';
import EmptyState from '../../src/components/common/EmptyState';
import SkeletonLoader from '../../src/components/common/SkeletonLoader';
import TagLegend from '../../src/components/common/TagLegend';
import ConfirmModal from '../../src/components/common/ConfirmModal';
import DateTimePicker from '../../src/components/common/DateTimePicker';
import CoachMark from '../../src/components/onboarding/CoachMark';
import { updateReminder } from '../../src/api/client';
import { useTheme, type ThemeColors } from '../../src/utils/theme';
import { FONT_SIZE, SPACING, RADIUS } from '../../src/utils/design-tokens';
import { formatRelativeTime } from '../../src/utils/localization';
import { compareOverdueNearestFirst, compareReminderTimesNewestFirst } from '../../src/utils/reminderOrdering';
import { hasSeenSwipeHint, markSwipeHintShown } from '../../src/utils/onboarding';
import { useBackHandler } from '../../src/navigation/useBackHandler';
import * as Haptics from 'expo-haptics';
import type { Reminder } from '../../src/types/api';

// ─── Helpers ───

type ReminderSection = 'overdue' | 'today' | 'upcoming' | 'paused' | 'done';

function classifyReminder(r: Reminder): ReminderSection {
  // Use server-side classification if available (§24)
  if (r.classification) return r.classification;
  if (r.is_paused) return 'paused';
  if (r.is_done) return 'done';
  if (!r.run_at_utc) return 'upcoming';
  const runDate = new Date(r.run_at_utc);
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const todayEnd = new Date(todayStart.getTime() + 86400000 - 1);
  if (runDate < todayStart) return 'overdue';
  if (runDate <= todayEnd) return 'today';
  return 'upcoming';
}

/** §24: Get a date key (YYYY-MM-DD local) for grouping */
function getDateKey(utcStr: string): string {
  const d = new Date(utcStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getMonthKey(utcStr: string): string {
  const date = new Date(utcStr);
  if (Number.isNaN(date.getTime())) return 'unknown';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function formatHistoryMonthTitle(monthKey: string, locale: string): string {
  if (monthKey === 'unknown') return 'Earlier';
  const [year, month] = monthKey.split('-').map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString(locale, {
    month: 'long',
    year: 'numeric',
  });
}

function formatHistoryDayTitle(utcStr: string, locale: string): string {
  const date = new Date(utcStr);
  if (Number.isNaN(date.getTime())) return 'No date';
  return date.toLocaleDateString(locale, {
    weekday: 'long',
    day: 'numeric',
  });
}

/** §24: Format date key into a friendly header */
function formatDateSectionTitle(dateKey: string, t: (k: string) => string): string {
  const [y, m, d] = dateKey.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today.getTime() + 86400000);
  const dayMs = date.getTime();

  if (dayMs === today.getTime()) {
    return `${t('reminders.today')} · ${date.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}`;
  }
  if (dayMs === tomorrow.getTime()) {
    return `${t('reminders.tomorrow')} · ${date.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}`;
  }
  // Within 6 days
  if (dayMs > today.getTime() && dayMs <= today.getTime() + 6 * 86400000) {
    return date.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
  }
  return date.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

/** §24: Status sort priority within a date section */
const STATUS_PRIORITY: Record<ReminderSection, number> = { overdue: 0, today: 1, upcoming: 1, done: 2, paused: 3 };
const OVERDUE_PREVIEW_LIMIT = 5;

interface ReminderListSection {
  title: string;
  key: string;
  data: Reminder[];
  totalCount?: number;
  kind?: 'history-month';
}

function reminderHasTag(reminder: Reminder, tag: string): boolean {
  const normalizedTag = tag.trim().toLowerCase();
  if (!normalizedTag) return false;
  return reminder.tags?.split(',').some((value) => value.trim().toLowerCase() === normalizedTag) ?? false;
}

function getReminderRescheduleValue(reminder: Reminder): string {
  const now = new Date();
  if (!reminder.run_at_utc) return now.toISOString();
  const runAt = new Date(reminder.run_at_utc);
  if (Number.isNaN(runAt.getTime()) || runAt <= now) return now.toISOString();
  return runAt.toISOString();
}

// ─── Component ───

export default function RemindersScreen() {
  const { t, i18n } = useTranslation();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const [searchOpen, setSearchOpen] = useState(false);
  const refreshProfile = useAuthStore((s) => s.refreshProfile);
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const tabBarBottomSpace = useNavigationStore((state) => state.tabBarBottomSpace);
  const tabBarKeyboardVisible = useNavigationStore((state) => state.tabBarKeyboardVisible);
  const reservedBottomSpace = tabBarBottomSpace > 0 ? tabBarBottomSpace : 46 + Math.max(insets.bottom, 6);
  const bottomChromeSpace = tabBarKeyboardVisible ? 0 : reservedBottomSpace;

  const {
    reminders,
    isLoading,
    isSyncing,
    filter,
    searchQuery,
    setFilter,
    setSearchQuery,
    syncFromServer,
    markDone,
    undoDone,
    snooze,
    pause,
    resume,
    skip,
    deleteReminder,
    lastSyncedAt,
  } = useRemindersStore(useShallow((s) => ({
    reminders: s.reminders,
    isLoading: s.isLoading,
    isSyncing: s.isSyncing,
    filter: s.filter,
    searchQuery: s.searchQuery,
    setFilter: s.setFilter,
    setSearchQuery: s.setSearchQuery,
    syncFromServer: s.syncFromServer,
    markDone: s.markDone,
    undoDone: s.undoDone,
    snooze: s.snooze,
    pause: s.pause,
    resume: s.resume,
    skip: s.skip,
    deleteReminder: s.deleteReminder,
    lastSyncedAt: s.lastSyncedAt,
  })));

  const [localSearch, setLocalSearch] = useState(searchQuery);
  const [isPullRefreshing, setIsPullRefreshing] = useState(false);
  const [pendingDeleteItem, setPendingDeleteItem] = useState<Reminder | null>(null);
  const [pendingDeleteSeq, setPendingDeleteSeq] = useState<number | null>(null);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [pendingRescheduleSeq, setPendingRescheduleSeq] = useState<number | null>(null);
  const [pendingReschedulePicker, setPendingReschedulePicker] = useState<{ reminder: Reminder; initialValue: string } | null>(null);
  const [pendingSnoozeSeq, setPendingSnoozeSeq] = useState<number | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [showSwipeHint, setShowSwipeHint] = useState(false);
  const [selectedSeqs, setSelectedSeqs] = useState<Set<number>>(new Set());
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [showAllOverdue, setShowAllOverdue] = useState(false);
  const [expandedHistoryMonths, setExpandedHistoryMonths] = useState<Set<string>>(new Set());
  const [headerCollapsed, setHeaderCollapsed] = useState(false);
  const headerCollapsedRef = useRef(false);

  // Show swipe hint on first visit if reminders exist
  useEffect(() => {
    if (reminders.length > 0) {
      hasSeenSwipeHint().then((seen) => {
        if (!seen) setShowSwipeHint(true);
      });
    }
  }, [reminders.length > 0]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (localSearch !== searchQuery) {
        setSearchQuery(localSearch);
      }
    }, 250);
    return () => clearTimeout(handler);
  }, [localSearch, searchQuery, setSearchQuery]);

  // Sync reminders from server when tab gains focus
  const checkGoogleStatus = useSettingsStore((s) => s.checkGoogleStatus);
  useFocusEffect(
    useCallback(() => {
      syncFromServer();
      refreshProfile();
      // Keep googleConnected fresh so the per-row sync badge is accurate.
      checkGoogleStatus();
    }, [refreshProfile, syncFromServer, checkGoogleStatus])
  );

  // ─── Filter counts (always from ALL reminders, not filtered) ───
  const stats = useMemo(() => {
    let active = 0, done = 0, todayCount = 0, pausedCount = 0;
    let weekCount = 0;
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const todayEnd = new Date(todayStart.getTime() + 86400000 - 1);
    const weekEnd = new Date();
    weekEnd.setDate(weekEnd.getDate() + 7);
    weekEnd.setHours(23, 59, 59, 999);
    for (const r of reminders) {
      const cls = classifyReminder(r);
      const runDate = r.run_at_utc ? new Date(r.run_at_utc) : null;
      const isDueToday = !!runDate && runDate >= todayStart && runDate <= todayEnd;
      const isDueThisWeek = !!runDate && runDate >= todayStart && runDate <= weekEnd;
      if (cls === 'done') done++;
      else if (cls === 'overdue') active++;
      else if (cls === 'paused') pausedCount++;
      else active++;
      if (isDueToday) todayCount++;
      if (isDueThisWeek) weekCount++;
    }
    return { active, done, todayCount, pausedCount, weekCount };
  }, [reminders]);

  // ─── Filter + search ───
  const filteredReminders = useMemo(() => {
    let list = reminders;

    // Apply client-side filter
    if (filter === 'today') {
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const todayEnd = new Date(todayStart.getTime() + 86400000 - 1);
      list = list.filter((r) => {
        if (!r.run_at_utc) return false;
        const runDate = new Date(r.run_at_utc);
        return runDate >= todayStart && runDate <= todayEnd;
      });
    } else if (filter === 'week') {
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekEnd = new Date();
      weekEnd.setDate(weekEnd.getDate() + 7);
      weekEnd.setHours(23, 59, 59, 999);
      list = list.filter((r) => {
        if (!r.run_at_utc) return false;
        const runDate = new Date(r.run_at_utc);
        return runDate >= todayStart && runDate <= weekEnd;
      });
    } else if (filter === 'history') {
      list = list.filter((r) => classifyReminder(r) === 'done');
    } else {
      // 'active' — everything except done
      list = list.filter((r) => classifyReminder(r) !== 'done');
    }

    // Apply search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter((r) =>
        r.body.toLowerCase().includes(q) ||
        (r.tags && r.tags.toLowerCase().includes(q))
      );
    }
    return list;
  }, [reminders, filter, searchQuery]);

  const tagFilteredReminders = useMemo(() => {
    if (!selectedTag) return filteredReminders;
    return filteredReminders.filter((reminder) => reminderHasTag(reminder, selectedTag));
  }, [filteredReminders, selectedTag]);

  const userTagColors = useAuthStore((s) => s.user?.tagColors ?? null);
  const googleConnected = useSettingsStore((s) => s.googleConnected);

  // ─── Sections (§24: date-based grouping) ───
  const sections = useMemo(() => {
    if (filter === 'history') {
      const monthGroups = new Map<string, Reminder[]>();
      const sortedHistory = [...tagFilteredReminders].sort(compareReminderTimesNewestFirst);

      for (const reminder of sortedHistory) {
        const monthKey = getMonthKey(reminder.run_at_utc);
        if (!monthGroups.has(monthKey)) monthGroups.set(monthKey, []);
        monthGroups.get(monthKey)!.push(reminder);
      }

      return Array.from(monthGroups.entries()).map(([monthKey, items]) => ({
        title: `${formatHistoryMonthTitle(monthKey, i18n.language)} (${items.length})`,
        key: `history-month:${monthKey}`,
        data: items,
        kind: 'history-month' as const,
      }));
    }

    // Group by date key
    const dateGroups = new Map<string, Reminder[]>();
    const overdueItems: Reminder[] = []; // overdue goes to a special leading section

    for (const r of tagFilteredReminders) {
      const cls = classifyReminder(r);
      if (cls === 'overdue') {
        overdueItems.push(r);
        continue;
      }
      const key = r.run_at_utc ? getDateKey(r.run_at_utc) : 'no-date';
      if (!dateGroups.has(key)) dateGroups.set(key, []);
      dateGroups.get(key)!.push(r);
    }

    // Sort date keys chronologically
    const sortedKeys = Array.from(dateGroups.keys()).sort();

    // Sort items within each group: status priority then time
    const byStatusThenTime = (a: Reminder, b: Reminder) => {
      const sa = STATUS_PRIORITY[classifyReminder(a)] ?? 5;
      const sb = STATUS_PRIORITY[classifyReminder(b)] ?? 5;
      if (sa !== sb) return sa - sb;
      return (a.run_at_utc || '').localeCompare(b.run_at_utc || '');
    };

    const result: ReminderListSection[] = [];

    // Overdue section first
    if (overdueItems.length) {
      overdueItems.sort(compareOverdueNearestFirst);
      result.push({
        title: `${t('reminders.overdue')} (${overdueItems.length})`,
        key: 'overdue',
        data: overdueItems,
      });
    }

    // Date-based sections
    for (const key of sortedKeys) {
      const items = dateGroups.get(key)!;
      items.sort(byStatusThenTime);
      const title = key === 'no-date'
        ? t('reminders.upcoming')
        : formatDateSectionTitle(key, t);
      result.push({
        title: `${title} (${items.length})`,
        key,
        data: items,
      });
    }

    return result;
  }, [filter, i18n.language, t, tagFilteredReminders]);

  const historySectionSignature = useMemo(
    () => sections
      .filter((section) => section.kind === 'history-month')
      .map((section) => section.key)
      .join('|'),
    [sections]
  );

  useEffect(() => {
    if (filter !== 'history') return;
    const latestMonth = sections.find((section) => section.kind === 'history-month');
    setExpandedHistoryMonths(new Set(latestMonth ? [latestMonth.key] : []));
  }, [filter, historySectionSignature, searchQuery, selectedTag]);

  const displaySections = useMemo(() => sections.map((section) => {
    const totalCount = section.data.length;
    if (section.kind === 'history-month' && !expandedHistoryMonths.has(section.key)) {
      return { ...section, data: [], totalCount };
    }
    if (collapsedSections.has(section.key)) return { ...section, data: [], totalCount };
    if (section.key === 'overdue' && !showAllOverdue && totalCount > OVERDUE_PREVIEW_LIMIT) {
      return { ...section, data: section.data.slice(0, OVERDUE_PREVIEW_LIMIT), totalCount };
    }
    return { ...section, totalCount };
  }), [sections, collapsedSections, expandedHistoryMonths, showAllOverdue]);

  useEffect(() => {
    setShowAllOverdue(false);
  }, [filter, searchQuery, selectedTag]);

  // Derive unique tags from reminders that are actually rendered.
  const visibleTags = useMemo(() => {
    const set = new Set<string>();
    for (const reminder of filteredReminders) {
      reminder.tags?.split(',').forEach((tag) => {
        const normalized = tag.trim().toLowerCase();
        if (normalized) set.add(normalized);
      });
    }
    return Array.from(set).sort();
  }, [filteredReminders]);

  const disabledTagFilters = useMemo(
    () => (selectedTag ? new Set(visibleTags.filter((tag) => tag !== selectedTag)) : undefined),
    [selectedTag, visibleTags]
  );

  useEffect(() => {
    if (!selectedTag || visibleTags.includes(selectedTag)) return;
    setSelectedTag(null);
  }, [selectedTag, visibleTags]);

  const selectionMode = selectedSeqs.size > 0;

  useBackHandler({
    id: 'reminders-tab',
    label: 'Reminders',
    priority: 700,
    enabled: !!pendingDeleteItem
      || pendingDeleteSeq !== null
      || confirmBulkDelete
      || pendingRescheduleSeq !== null
      || !!pendingReschedulePicker
      || pendingSnoozeSeq !== null
      || showSwipeHint
      || selectionMode
      || !!selectedTag
      || showAllOverdue
      || searchOpen,
    onBack: () => {
      if (pendingDeleteItem) {
        setPendingDeleteItem(null);
        return true;
      }
      if (pendingDeleteSeq !== null) {
        setPendingDeleteSeq(null);
        return true;
      }
      if (confirmBulkDelete) {
        setConfirmBulkDelete(false);
        return true;
      }
      if (pendingRescheduleSeq !== null) {
        setPendingRescheduleSeq(null);
        return true;
      }
      if (pendingReschedulePicker) {
        setPendingReschedulePicker(null);
        return true;
      }
      if (pendingSnoozeSeq !== null) {
        setPendingSnoozeSeq(null);
        return true;
      }
      if (showSwipeHint) {
        setShowSwipeHint(false);
        return true;
      }
      if (selectionMode) {
        setSelectedSeqs(new Set());
        return true;
      }
      if (showAllOverdue) {
        LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
        setShowAllOverdue(false);
        return true;
      }
      if (selectedTag) {
        setSelectedTag(null);
        return true;
      }
      if (searchOpen) {
        setSearchOpen(false);
        setLocalSearch('');
        setSearchQuery('');
        return true;
      }
      return false;
    },
  });
  const selectedReminders = useMemo(
    () => reminders.filter((reminder) => selectedSeqs.has(reminder.user_seq)),
    [reminders, selectedSeqs]
  );
  const bulkActionReminders = useMemo(
    () => selectedReminders.filter((reminder) => classifyReminder(reminder) !== 'done' && !reminder.is_paused),
    [selectedReminders]
  );
  const visibleReminderSeqs = useMemo(
    () => displaySections.flatMap((section) => section.data.map((reminder) => reminder.user_seq)),
    [displaySections]
  );

  useEffect(() => {
    if (selectedSeqs.size === 0) return;
    const existingSeqs = new Set(reminders.map((reminder) => reminder.user_seq));
    setSelectedSeqs((current) => {
      const next = new Set(Array.from(current).filter((seq) => existingSeqs.has(seq)));
      return next.size === current.size ? current : next;
    });
  }, [reminders, selectedSeqs.size]);

  const clearSelection = useCallback(() => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setSelectedSeqs(new Set());
  }, []);

  const toggleSelection = useCallback((seq: number) => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setSelectedSeqs((current) => {
      const next = new Set(current);
      if (next.has(seq)) next.delete(seq);
      else next.add(seq);
      return next;
    });
  }, []);

  const enterSelectionMode = useCallback((seq: number) => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setSelectedSeqs((current) => {
      const next = new Set(current);
      next.add(seq);
      return next;
    });
  }, []);

  const selectVisibleReminders = useCallback(() => {
    Haptics.selectionAsync();
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setSelectedSeqs((current) => {
      const next = new Set(current);
      visibleReminderSeqs.forEach((seq) => next.add(seq));
      return next;
    });
  }, [visibleReminderSeqs]);

  const handleBulkDone = useCallback(async () => {
    const seqs = bulkActionReminders.map((reminder) => reminder.user_seq);
    clearSelection();
    for (const seq of seqs) {
      await markDone(seq, { suppressUndoPrompt: true });
    }
  }, [bulkActionReminders, clearSelection, markDone]);

  const handleBulkSnooze = useCallback(async (minutes: number) => {
    const seqs = bulkActionReminders.map((reminder) => reminder.user_seq);
    clearSelection();
    for (const seq of seqs) {
      await snooze(seq, minutes);
    }
  }, [bulkActionReminders, clearSelection, snooze]);

  const handleBulkDelete = useCallback(async () => {
    const seqs = selectedReminders.map((reminder) => reminder.user_seq);
    setConfirmBulkDelete(false);
    clearSelection();
    for (const seq of seqs) {
      await deleteReminder(seq);
    }
  }, [clearSelection, deleteReminder, selectedReminders]);

  const handlePress = useCallback(
    (reminder: Reminder) => {
      if (selectionMode) {
        Haptics.selectionAsync();
        toggleSelection(reminder.user_seq);
        return;
      }
      Haptics.selectionAsync();
      router.push({
        pathname: '/reminder/[seq]',
        params: { seq: reminder.user_seq.toString() },
      });
    },
    [router, selectionMode, toggleSelection]
  );

  const handleTagPress = useCallback((tag: string) => {
    Haptics.selectionAsync();
    const normalizedTag = tag.trim().toLowerCase();
    setSelectedTag((current) => current === normalizedTag ? null : normalizedTag);
  }, []);

  const handleReschedulePickerConfirm = useCallback(async (iso: string) => {
    const pending = pendingReschedulePicker;
    if (!pending) return;
    setPendingReschedulePicker(null);
    try {
      await updateReminder(pending.reminder.user_seq, { runAtUtc: iso });
      await syncFromServer();
    } catch (error) {
      console.warn('[Reminders] reschedule failed', error);
      Alert.alert(
        t('auth.error', 'Error'),
        t('reminder.rescheduleFailed', 'Failed to reschedule reminder'),
      );
    }
  }, [pendingReschedulePicker, syncFromServer, t]);

  const renderItem = useCallback(
    ({ item, index, section }: { item: Reminder; index: number; section: ReminderListSection }) => {
      const previousItem = index > 0 ? section.data[index - 1] : null;
      const showHistoryDay = section.kind === 'history-month'
        && (!previousItem || getDateKey(previousItem.run_at_utc) !== getDateKey(item.run_at_utc));

      return (
      <View>
        {showHistoryDay ? (
          <View style={styles.historyDayHeader}>
            <Text style={styles.historyDayTitle}>{formatHistoryDayTitle(item.run_at_utc, i18n.language)}</Text>
            <View style={styles.historyDayRule} />
          </View>
        ) : null}
        <ReminderRow
          reminder={item}
          onPress={() => handlePress(item)}
          onLongPress={() => enterSelectionMode(item.user_seq)}
          selectionMode={selectionMode}
          selected={selectedSeqs.has(item.user_seq)}
          tagColors={userTagColors}
          onDone={() => markDone(item.user_seq)}
          onUndo={async () => {
            const result = await undoDone(item.user_seq);
            if (result.isOverdue) {
              setPendingRescheduleSeq(item.user_seq);
            }
          }}
          onReschedule={() => {
            Haptics.selectionAsync();
            setPendingReschedulePicker({ reminder: item, initialValue: getReminderRescheduleValue(item) });
          }}
          onSnooze={!item.is_recurring ? () => setPendingSnoozeSeq(item.user_seq) : undefined}
          onPause={item.is_recurring ? () => pause(item.user_seq) : undefined}
          onResume={() => resume(item.user_seq)}
          onDelete={() => {
            if (item.is_recurring) {
              setPendingDeleteItem(item);
            } else {
              setPendingDeleteSeq(item.user_seq);
            }
          }}
          relativeTime={item.run_at_utc ? formatRelativeTime(item.run_at_utc, i18n.language) : undefined}
          onTagPress={handleTagPress}
          googleConnected={googleConnected}
        />
      </View>
      );
    },
    [enterSelectionMode, handlePress, handleTagPress, i18n.language, markDone, pause, resume, selectedSeqs, selectionMode, styles, undoDone, userTagColors, googleConnected]
  );

  const handleDeleteConfirmAction = useCallback((action: 'skip' | 'delete') => {
    if (!pendingDeleteItem) return;
    const seq = pendingDeleteItem.user_seq;
    setPendingDeleteItem(null);
    if (action === 'skip') skip(seq);
    else deleteReminder(seq);
  }, [pendingDeleteItem, skip, deleteReminder]);

  const renderSectionHeader = useCallback(
    ({ section }: { section: ReminderListSection }) => {
      const isHistoryMonth = section.kind === 'history-month';
      const isCollapsed = isHistoryMonth
        ? !expandedHistoryMonths.has(section.key)
        : collapsedSections.has(section.key);
      return (
        <TouchableOpacity
          activeOpacity={0.7}
          onPress={() => {
            LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
            if (isHistoryMonth) {
              setExpandedHistoryMonths(prev => {
                const next = new Set(prev);
                if (next.has(section.key)) next.delete(section.key);
                else next.add(section.key);
                return next;
              });
              return;
            }
            setCollapsedSections(prev => {
              const next = new Set(prev);
              if (next.has(section.key)) next.delete(section.key);
              else next.add(section.key);
              return next;
            });
          }}
          style={[
            styles.sectionHeader,
            section.key === 'overdue' && styles.overdueSectionHeader,
          ]}
        >
          <Text style={[
            styles.sectionTitle,
            section.key === 'overdue' && styles.overdueSectionTitle,
          ]}>
            {section.title}
          </Text>
          <Ionicons
            name={isCollapsed ? 'chevron-forward' : 'chevron-down'}
            size={16}
            color={section.key === 'overdue' ? '#FF6B6B' : '#999'}
          />
        </TouchableOpacity>
      );
    },
    [collapsedSections, expandedHistoryMonths, styles]
  );

  const renderSectionFooter = useCallback(
    ({ section }: { section: ReminderListSection }) => {
      const totalCount = section.totalCount ?? section.data.length;
      if (
        section.key !== 'overdue'
        || totalCount <= OVERDUE_PREVIEW_LIMIT
        || collapsedSections.has(section.key)
      ) return null;

      const label = showAllOverdue
        ? t('reminders.showFewer', 'Show fewer')
        : t('reminders.showAllCount', { count: totalCount, defaultValue: `Show all ${totalCount}` });
      return (
        <TouchableOpacity
          style={styles.overdueReviewButton}
          activeOpacity={0.72}
          onPress={() => {
            Haptics.selectionAsync();
            LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
            setShowAllOverdue((current) => !current);
          }}
          accessibilityRole="button"
          accessibilityLabel={label}
          accessibilityState={{ expanded: showAllOverdue }}
        >
          <Text style={styles.overdueReviewText}>{label}</Text>
          <Ionicons name={showAllOverdue ? 'chevron-up' : 'chevron-down'} size={16} color={colors.error} />
        </TouchableOpacity>
      );
    },
    [collapsedSections, colors.error, showAllOverdue, styles, t]
  );

  const keyExtractor = useCallback(
    (item: Reminder) => `r-${item.user_seq}`,
    []
  );

  // ─── Sync status ───
  const wsState = useChatStore(s => s.wsState);
  const isOffline = wsState !== 'connected';
  const handleSyncPress = useCallback(() => {
    useChatStore.getState().initWebSocket();
    syncFromServer();
  }, [syncFromServer]);
  const handlePullRefresh = useCallback(async () => {
    setIsPullRefreshing(true);
    try {
      await syncFromServer();
    } finally {
      setIsPullRefreshing(false);
    }
  }, [syncFromServer]);

  const handleListScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const offsetY = event.nativeEvent.contentOffset.y;
    const nextCollapsed = headerCollapsedRef.current ? offsetY > 4 : offsetY > 24;
    if (nextCollapsed === headerCollapsedRef.current) return;

    headerCollapsedRef.current = nextCollapsed;
    LayoutAnimation.configureNext({
      duration: 180,
      create: {
        type: LayoutAnimation.Types.easeInEaseOut,
        property: LayoutAnimation.Properties.opacity,
      },
      update: { type: LayoutAnimation.Types.easeInEaseOut },
      delete: {
        type: LayoutAnimation.Types.easeInEaseOut,
        property: LayoutAnimation.Properties.opacity,
      },
    });
    setHeaderCollapsed(nextCollapsed);
  }, []);

  return (
    <View style={[styles.container, { paddingTop: insets.top, paddingBottom: bottomChromeSpace }]}>
      {/* Compact task header */}
      <View style={[styles.compactHeader, headerCollapsed && styles.compactHeaderCollapsed]}>
        <View style={styles.compactTitleRow}>
          <View style={styles.compactTitleBlock}>
            <View style={styles.compactTitleStatusRow}>
              <Text style={[styles.compactTitle, headerCollapsed && styles.compactTitleCollapsed]}>{t('tabs.reminders', 'Reminders')}</Text>
              {(isLoading || isSyncing) && !isPullRefreshing ? (
                <View
                  style={styles.syncIndicator}
                  accessible
                  accessibilityRole="progressbar"
                  accessibilityLabel={t('reminders.syncing', 'Refreshing reminders')}
                >
                  <ActivityIndicator size="small" color={colors.primary} />
                </View>
              ) : !isLoading && !isSyncing && !isOffline && lastSyncedAt ? (
                <TouchableOpacity
                  onPress={handleSyncPress}
                  style={styles.syncIndicator}
                  hitSlop={8}
                  accessibilityRole="button"
                  accessibilityLabel={t('reminders.syncedRefresh', 'Reminders synced. Tap to refresh')}
                >
                  <Ionicons name="checkmark-circle" size={18} color={colors.success} />
                </TouchableOpacity>
              ) : null}
            </View>
            {!headerCollapsed && isOffline ? (
              <TouchableOpacity onPress={handleSyncPress} style={styles.syncStatus} hitSlop={6}>
                <Ionicons name="cloud-offline-outline" size={13} color={colors.warning} />
                <Text style={[styles.compactSubtitle, { color: colors.warning }]}>
                  {t('reminders.pendingSync', { defaultValue: 'Offline — tap to sync' })}
                </Text>
              </TouchableOpacity>
            ) : null}
          </View>
          <TouchableOpacity
            onPress={() => {
              Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
              LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
              setSearchOpen(!searchOpen);
            }}
            style={styles.compactSearchToggle}
            hitSlop={6}
            accessibilityRole="button"
            accessibilityLabel={searchOpen ? t('common.close') : t('common.search')}
          >
            <Ionicons name={searchOpen ? 'close' : 'search'} size={20} color={colors.text} />
          </TouchableOpacity>
        </View>

      </View>

      {/* Search bar */}
      {searchOpen && (
        <View style={styles.searchRow}>
          <TextInput
            style={styles.searchInput}
            placeholder={t('reminders.search')}
            placeholderTextColor={colors.textSecondary}
            value={localSearch}
            onChangeText={setLocalSearch}
            autoFocus
            returnKeyType="search"
          />
        </View>
      )}

      {/* Filters */}
      <ReminderFilters
        selected={filter}
        onSelect={(f) => {
          Haptics.selectionAsync();
          setFilter(f);
        }}
        counts={{
          active: stats.active + stats.pausedCount,
          today: stats.todayCount,
          week: stats.weekCount,
          history: stats.done,
        }}
      />

      {visibleTags.length > 0 && (
        <View style={styles.tagLegendWrap}>
          <TagLegend
            tags={visibleTags}
            tagColors={userTagColors}
            variant="light"
            onPressTag={handleTagPress}
            disabledTags={disabledTagFilters}
            activeTag={selectedTag}
          />
        </View>
      )}

      {selectionMode && (
        <View style={styles.selectionBar}>
          <Text style={styles.selectionCount} numberOfLines={1}>
            {t('reminders.selectedCount', { count: selectedSeqs.size, defaultValue: `${selectedSeqs.size} selected` })}
          </Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.selectionActionsScroller} contentContainerStyle={styles.selectionActions}>
            <BulkActionButton icon="checkbox-outline" label={t('common.all', 'All')} onPress={selectVisibleReminders} colors={colors} />
            <BulkActionButton icon="checkmark" label={t('reminder.done', 'Done')} onPress={handleBulkDone} colors={colors} disabled={bulkActionReminders.length === 0} />
            <BulkActionButton icon="time-outline" label="15m" onPress={() => handleBulkSnooze(15)} colors={colors} disabled={bulkActionReminders.length === 0} />
            <BulkActionButton icon="trash-outline" label={t('common.delete', 'Delete')} onPress={() => setConfirmBulkDelete(true)} colors={colors} danger iconOnly />
          </ScrollView>
          <TouchableOpacity
            onPress={clearSelection}
            style={styles.selectionClearButton}
            accessibilityRole="button"
            accessibilityLabel={t('common.cancel', 'Cancel')}
          >
            <Ionicons name="close" size={15} color={colors.textSecondary} />
          </TouchableOpacity>
        </View>
      )}

      {/* Sectioned List */}
      <SectionList
        sections={displaySections}
        renderItem={renderItem}
        renderSectionHeader={renderSectionHeader}
        renderSectionFooter={renderSectionFooter}
        keyExtractor={keyExtractor}
        extraData={[userTagColors, selectedSeqs, selectionMode, showAllOverdue, expandedHistoryMonths]}
        style={styles.list}
        contentContainerStyle={
          sections.length === 0 ? styles.emptyList : styles.listContent
        }
        stickySectionHeadersEnabled={false}
        initialNumToRender={10}
        windowSize={5}
        maxToRenderPerBatch={10}
        updateCellsBatchingPeriod={50}
        onScroll={handleListScroll}
        scrollEventThrottle={16}
        refreshControl={
          <RefreshControl refreshing={isPullRefreshing} onRefresh={handlePullRefresh} tintColor={colors.primary} colors={[colors.primary]} />
        }
        ListEmptyComponent={
          isLoading ? (
            <SkeletonLoader variant="reminder-list" rows={5} />
          ) : searchQuery ? (
            <EmptyState
              emoji="🔍"
              title={t('reminders.noResults', 'No reminders match your search')}
              subtitle={t('reminders.noResultsHint', 'Try a different keyword or clear the search')}
            />
          ) : (
            <EmptyState
              emoji="🐟"
              title={t('reminders.emptyTitle', 'No reminders yet')}
              subtitle={t('reminders.emptySubtitle', 'Your first reminder is just a message away')}
              actionLabel={t('reminders.emptyAction')}
              onAction={() => router.push('/(tabs)/chat')}
            />
          )
        }
      />

      {/* Recurring delete confirmation */}
      <ConfirmModal
        visible={!!pendingDeleteItem}
        title={t('reminder.deleteRecurringTitle')}
        message={t('reminder.deleteRecurringMessage')}
        actions={[
          { label: t('reminder.deleteThisOccurrence'), onPress: () => handleDeleteConfirmAction('skip'), variant: 'primary' },
          { label: t('reminder.deleteEntireSeries'), onPress: () => handleDeleteConfirmAction('delete'), variant: 'danger' },
        ]}
        cancelLabel={t('common.cancel')}
        onCancel={() => setPendingDeleteItem(null)}
        dismissOnBackdrop={false}
      />

      {/* Snooze duration picker */}
      <ConfirmModal
        visible={pendingSnoozeSeq !== null}
        title={t('reminder.snoozeTitle', { defaultValue: 'Snooze for...' })}
        message=""
        actions={[
          { label: '15 min', onPress: () => { if (pendingSnoozeSeq) snooze(pendingSnoozeSeq, 15); setPendingSnoozeSeq(null); }, variant: 'primary' },
          { label: '1 hour', onPress: () => { if (pendingSnoozeSeq) snooze(pendingSnoozeSeq, 60); setPendingSnoozeSeq(null); }, variant: 'primary' },
          { label: '3 hours', onPress: () => { if (pendingSnoozeSeq) snooze(pendingSnoozeSeq, 180); setPendingSnoozeSeq(null); }, variant: 'primary' },
          { label: t('reminder.snoozeTomorrow', { defaultValue: 'Tomorrow 9 AM' }), onPress: () => { if (pendingSnoozeSeq) { const tom = new Date(); tom.setDate(tom.getDate() + 1); tom.setHours(9, 0, 0, 0); const mins = Math.round((tom.getTime() - Date.now()) / 60000); snooze(pendingSnoozeSeq, mins); } setPendingSnoozeSeq(null); }, variant: 'primary' },
        ]}
        cancelLabel={t('common.cancel')}
        onCancel={() => setPendingSnoozeSeq(null)}
      />

      {/* Single delete confirmation */}
      {(() => {
        const delReminder = pendingDeleteSeq != null ? reminders.find(r => r.user_seq === pendingDeleteSeq) : null;
        const isSynced = googleConnected && !!delReminder?.google_event_id;
        const actions = isSynced
          ? [
              { label: t('reminder.deleteBoth', 'Delete from app & Google'), variant: 'danger' as const, onPress: () => { if (pendingDeleteSeq) deleteReminder(pendingDeleteSeq, { googleAction: 'delete' }); setPendingDeleteSeq(null); } },
              { label: t('reminder.deleteAppOnly', 'Delete from app only'), variant: 'danger' as const, onPress: () => { if (pendingDeleteSeq) deleteReminder(pendingDeleteSeq, { googleAction: 'keep' }); setPendingDeleteSeq(null); } },
            ]
          : [
              { label: t('common.delete', 'Delete'), variant: 'danger' as const, onPress: () => { if (pendingDeleteSeq) deleteReminder(pendingDeleteSeq); setPendingDeleteSeq(null); } },
            ];
        return (
          <ConfirmModal
            visible={pendingDeleteSeq !== null}
            title={t('reminder.delete')}
            message={isSynced
              ? t('reminder.deleteConfirmSynced', 'This reminder is also on your Google Calendar. What should happen to it?')
              : t('reminder.confirmDelete', 'Delete this reminder?')}
            actions={actions}
            cancelLabel={t('common.cancel')}
            onCancel={() => setPendingDeleteSeq(null)}
            dismissOnBackdrop={false}
          />
        );
      })()}

      <ConfirmModal
        visible={confirmBulkDelete}
        title={t('reminders.deleteSelectedTitle', 'Delete selected reminders?')}
        message={t('reminders.deleteSelectedMessage', {
          count: selectedSeqs.size,
          defaultValue: 'This deletes the selected reminders. Recurring selections delete the full series.',
        })}
        actions={[
          { label: t('common.delete', 'Delete'), onPress: handleBulkDelete, variant: 'danger' },
        ]}
        cancelLabel={t('common.cancel')}
        onCancel={() => setConfirmBulkDelete(false)}
        dismissOnBackdrop={false}
      />

      <DateTimePicker
        visible={!!pendingReschedulePicker}
        value={pendingReschedulePicker?.initialValue ?? new Date().toISOString()}
        title={t('reminder.pickDateTime', 'Pick date & time')}
        minDate={new Date().toISOString()}
        onConfirm={(iso) => { void handleReschedulePickerConfirm(iso); }}
        onCancel={() => setPendingReschedulePicker(null)}
      />

      {/* Reschedule after undo */}
      <ConfirmModal
        visible={pendingRescheduleSeq !== null}
        title={t('reminder.rescheduleTitle')}
        message={t('reminder.rescheduleMsg')}
        actions={[
          { label: t('reminder.reschedule15'), onPress: () => { if (pendingRescheduleSeq) snooze(pendingRescheduleSeq, 15); setPendingRescheduleSeq(null); }, variant: 'primary' },
          { label: t('reminder.reschedule60'), onPress: () => { if (pendingRescheduleSeq) snooze(pendingRescheduleSeq, 60); setPendingRescheduleSeq(null); }, variant: 'primary' },
          { label: t('reminder.keepOverdue'), onPress: () => setPendingRescheduleSeq(null), variant: 'outline' },
        ]}
        cancelLabel={t('common.cancel')}
        onCancel={() => setPendingRescheduleSeq(null)}
      />

      {/* Swipe gesture coach mark */}
      <CoachMark
        visible={showSwipeHint}
        text={t('onboarding.swipeHint')}
        onDismiss={() => { markSwipeHintShown(); setShowSwipeHint(false); }}
      />
    </View>
  );
}

function BulkActionButton({ icon, label, onPress, colors, disabled, danger, iconOnly }: { icon: keyof typeof Ionicons.glyphMap; label: string; onPress: () => void; colors: ThemeColors; disabled?: boolean; danger?: boolean; iconOnly?: boolean }) {
  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={disabled}
      activeOpacity={0.75}
      style={[
        bulkActionStyles.button,
        iconOnly && bulkActionStyles.iconOnlyButton,
        { borderColor: danger ? colors.danger + '40' : colors.border, backgroundColor: danger ? colors.danger + '0F' : colors.surface },
        disabled && { opacity: 0.45 },
      ]}
      accessibilityRole="button"
      accessibilityLabel={label}
    >
      <Ionicons name={icon} size={15} color={danger ? colors.danger : colors.primary} />
      {!iconOnly ? <Text style={[bulkActionStyles.text, { color: danger ? colors.danger : colors.text }]} numberOfLines={1}>{label}</Text> : null}
    </TouchableOpacity>
  );
}

const bulkActionStyles = StyleSheet.create({
  button: {
    height: 28,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingHorizontal: 8,
    borderRadius: RADIUS.full,
    borderWidth: 0.5,
  },
  iconOnlyButton: {
    width: 32,
    paddingHorizontal: 0,
  },
  text: {
    fontSize: 10,
    fontWeight: '700',
  },
});

// ─── Styles ───
const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  compactHeader: {
    marginHorizontal: SPACING.base,
    marginTop: SPACING.xs,
    paddingTop: SPACING.xs,
    paddingBottom: 6,
  },
  compactHeaderCollapsed: {
    marginTop: 0,
    paddingTop: 0,
    paddingBottom: SPACING.xs,
  },
  compactTitleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  compactTitleBlock: {
    flex: 1,
    minWidth: 0,
  },
  compactTitleStatusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
  },
  compactTitle: {
    fontSize: FONT_SIZE.xl,
    fontFamily: 'PlusJakartaSans_600SemiBold',
    color: colors.text,
    letterSpacing: -0.3,
  },
  compactTitleCollapsed: {
    fontSize: FONT_SIZE.lg,
  },
  syncIndicator: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  compactSubtitle: {
    fontSize: FONT_SIZE.xs,
    fontFamily: 'Inter_400Regular',
    color: colors.textSecondary,
    marginTop: 1,
    letterSpacing: 0,
  },
  syncStatus: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
  },
  compactSearchToggle: {
    width: 44,
    height: 44,
    borderRadius: 22,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.card,
    borderWidth: 1,
    borderColor: colors.border,
  },
  searchRow: {
    paddingHorizontal: 20,
    paddingBottom: 6,
  },
  searchInput: {
    backgroundColor: colors.surface,
    borderRadius: RADIUS.md,
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.md,
    fontSize: FONT_SIZE.sm,
    color: colors.text,
    borderWidth: 0.5,
    borderColor: colors.border,
  },

  tagLegendWrap: {
    paddingHorizontal: 16,
    marginBottom: 4,
  },
  selectionBar: {
    marginHorizontal: SPACING.base,
    marginBottom: SPACING.xs,
    paddingHorizontal: SPACING.sm,
    paddingVertical: 7,
    borderRadius: RADIUS.lg,
    borderWidth: 0.5,
    borderColor: colors.primary + '24',
    backgroundColor: colors.card,
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
  },
  selectionCount: {
    minWidth: 66,
    fontSize: FONT_SIZE.sm,
    fontWeight: '700',
    color: colors.text,
  },
  selectionClearButton: {
    width: 24,
    height: 24,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.surface,
  },
  selectionActionsScroller: {
    flex: 1,
    minWidth: 0,
  },
  selectionActions: {
    alignItems: 'center',
    gap: 5,
    paddingRight: 2,
  },
  sectionHeader: {
    paddingHorizontal: SPACING.lg,
    paddingTop: SPACING.base,
    paddingBottom: 6,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  overdueSectionHeader: {
    backgroundColor: colors.error + '14',
    marginHorizontal: SPACING.base,
    borderRadius: RADIUS.md,
    paddingVertical: SPACING.sm,
    marginTop: SPACING.sm,
  },
  sectionTitle: {
    fontSize: FONT_SIZE.xs,
    fontFamily: 'Inter_600SemiBold',
    color: colors.textSecondary,
    letterSpacing: 0,
    textTransform: 'uppercase',
  },
  historyDayHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
    paddingHorizontal: SPACING.lg,
    paddingTop: SPACING.sm,
    paddingBottom: SPACING.xs,
  },
  historyDayTitle: {
    fontSize: FONT_SIZE.xs,
    fontFamily: 'Inter_600SemiBold',
    color: colors.textSecondary,
  },
  historyDayRule: {
    flex: 1,
    height: StyleSheet.hairlineWidth,
    backgroundColor: colors.border,
  },
  overdueSectionTitle: {
    color: colors.error,
  },
  overdueReviewButton: {
    alignSelf: 'center',
    minHeight: 44,
    marginTop: SPACING.xs,
    marginBottom: SPACING.xs,
    paddingHorizontal: SPACING.base,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: SPACING.xs,
    borderRadius: RADIUS.full,
    backgroundColor: colors.error + '0D',
  },
  overdueReviewText: {
    fontSize: FONT_SIZE.sm,
    fontFamily: 'Inter_600SemiBold',
    color: colors.error,
  },
  list: {
    flex: 1,
  },
  listContent: {
    paddingBottom: SPACING.lg,
  },
  emptyList: {
    flex: 1,
  },
});
